import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowRoyalCollectIntro,
  setRoyalCollectTransitionActive,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import SpineAtlasUploader from "../../../../utils/SpineAtlasUploader";

// Device and orientation-specific size configuration for the Royal Collect intro dialog
// Reuses same layout as FreeSpinIntroDialog since they share the same spine asset
const INTRO_SIZE_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.85,
      buttonFontScale: 1.3,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.05,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.75,
      buttonFontScale: 1.2,
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.2,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 1.35,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 1.35,
    },
  },
  DESKTOP: {
    spineScaleMultiplier: 1.0,
    spineYOffset: 0,
    buttonYOffsetMultiplier: 1.0,
    buttonFontScale: 1.0,
  },
};

/**
 * RoyalCollectIntroDialogController — Handles Royal Collect intro animation sequence
 * Reuses the same dialog spine asset as Free Spins (temporary — will get its own spine later)
 * Requires manual user interaction to close (button click or overlay click)
 */
class RoyalCollectIntroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;
    this.buttonAnim = null; // Track active anime instance on button

    // Button attached to spine slot
    this.buttonContainer = null;
    this.buttonShadowText = null;
    this.buttonText = null;

    // Internal caches
    this._configCache = null;
    this._deviceScaleMultiplierCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeIntroSpine();
  }

  getConfig() {
    const { device, orientation } = this.dimensionsContext;

    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._configCache;
    }

    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) {
      deviceKey = "MOBILE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      deviceKey = "TABLET";
    } else {
      deviceKey = "DESKTOP";
    }

    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    const deviceConfig =
      INTRO_SIZE_CONFIG[deviceKey] || INTRO_SIZE_CONFIG.DESKTOP;
    const config = deviceConfig[orientationKey] || deviceConfig;

    this._configCache = config;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return config;
  }

  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions) return;

    const config = this.getConfig();

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    scale *= config.spineScaleMultiplier;

    this.introSpine.scale.set(scale);
    this.introSpine.x = 0;
    this.introSpine.y = config.spineYOffset || 0;

    this.updateButtonPosition();
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._configCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }
    this.updateButtonStyle();

    if (
      this.introSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      this.introSpine.alpha = 0.001;
      this.introSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      this._availableAnimationsCache = null;

      this.warmUpIntroSpine();

      // Force-upload all dialog atlas page textures to GPU
      SpineAtlasUploader.upload("dialogs");

      this.initializeButton();
      this.initializeTexts();
    } catch (error) {
      // Silent fail
    }
  }

  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "dialogs-skeleton";
      const atlasAlias = "dialogs-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) {
        return null;
      }

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  warmUpIntroSpine() {
    if (!this.introSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.introSpine.state.setAnimation(0, animations[0], false);
        this.introSpine.state.update(0.016);
        this.introSpine.skeleton.updateWorldTransform();
      }
      this.introSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.introSpine?.skeleton?.data?.animations) return [];

    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    const animations = this.introSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  initializeButton() {
    if (!this.introSpine || !this.introSpine.skeleton) {
      return;
    }

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "royal_collect_intro_continue_overlay";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001;

    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const fontSize = this._computeButtonFontSize();
    const scaled = this._computeScaledStrokeParams(fontSize);
    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
    };

    this.buttonShadowText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.shadow,
        strokeThickness: scaled.shadowStroke,
      }),
    );
    this.buttonShadowText.resolution = 2;
    this.buttonShadowText.anchor.set(0.5);
    this.buttonShadowText.y = scaled.shadowOffsetY;

    this.buttonText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.top,
        strokeThickness: scaled.topStroke,
      }),
    );
    this.buttonText.resolution = 2;
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonShadowText);
    this.buttonContainer.addChild(this.buttonText);

    this.container.addChild(this.buttonContainer);

    this.updateButtonPosition();
  }

  updateButtonPosition() {
    if (!this.buttonContainer || !this.dimensions) return;

    const config = this.getConfig();
    const multiplier = config.buttonYOffsetMultiplier ?? 1.0;

    const buttonY = this.dimensions.height / 2 - this.symbolHeight * multiplier;

    this.buttonContainer.x = 0;
    this.buttonContainer.y = buttonY;
  }

  updateButtonStyle() {
    if (!this.buttonText || !this.buttonShadowText) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const fontSize = this._computeButtonFontSize();
    const scaled = this._computeScaledStrokeParams(fontSize);

    this.buttonShadowText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.shadow,
      strokeThickness: scaled.shadowStroke,
    });
    this.buttonShadowText.y = scaled.shadowOffsetY;

    this.buttonText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.top,
      strokeThickness: scaled.topStroke,
    });
  }

  initializeTexts() {
    if (!this.introSpine?.skeleton) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;

    // CONGRATULATIONS — PRIMARY two-layer, curved arc
    this.initializeCurvedCongratsText(primaryStyle, slotText);

    // YOU HAVE WON — PRIMARY two-layer
    this.initializePrimarySlotText(
      "txt_slot_youwon",
      this.t("you_won").toUpperCase(),
      primaryStyle,
      slotText,
      52,
    );

    // ROYAL COLLECT — PRIMARY two-layer (reuse free_spins slot, different text)
    this.initializePrimarySlotText(
      "txt_slot_free_games",
      this.t("royal_collect", { defaultValue: "ROYAL COLLECT" }).toUpperCase(),
      primaryStyle,
      slotText,
      52,
    );
  }

  _computeButtonFontSize() {
    const baseFontSize = 32;
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const spineRatio = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    const config = this.getConfig();
    return Math.round(
      baseFontSize *
        spineRatio *
        (config.spineScaleMultiplier || 1.0) *
        (config.buttonFontScale || 1.0),
    );
  }

  _computeScaledStrokeParams(fontSize) {
    const baseFontSize = 32;
    const ratio = Math.max(0.3, fontSize / baseFontSize);
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    return {
      shadowStroke: Math.max(1, Math.round((primaryStyle.shadow.strokeThickness || 4) * ratio)),
      topStroke: Math.max(1, Math.round((primaryStyle.top.strokeThickness || 3) * ratio)),
      shadowOffsetY: Math.max(1, Math.round((primaryStyle.shadowOffsetY || 3) * ratio)),
    };
  }

  initializeCurvedCongratsText(primaryStyle, slotText) {
    const slot = this.introSpine.skeleton.findSlot("txt_slot_congrats");
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = "txt_slot_congrats_curved_text";
    container.zIndex = 1000;

    const text = this.t("congratulations").toUpperCase();
    const fontSize = 58;
    const arcRadius = 2100;
    const totalArcAngle = 0.3;
    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "800",
    };

    const shadowStyle = new PIXI.TextStyle({
      ...sharedProps,
      ...primaryStyle.shadow,
    });
    const topStyle = new PIXI.TextStyle({
      ...sharedProps,
      ...primaryStyle.top,
    });

    const charWidths = [];
    for (const char of text) {
      const metric = PIXI.TextMetrics.measureText(char, topStyle);
      charWidths.push(metric.width);
    }

    const letterSpacingExtra = 14;

    const rawAngularWidths = charWidths.map(
      (w) => (w + letterSpacingExtra) / arcRadius,
    );
    const rawTotalAngle = rawAngularWidths.reduce((sum, a) => sum + a, 0);
    const scaleFactor = totalArcAngle / rawTotalAngle;
    const angularWidths = rawAngularWidths.map((a) => a * scaleFactor);

    const startAngle = -Math.PI / 2 - totalArcAngle / 2;
    let currentAngle = startAngle + angularWidths[0] / 2;

    for (let i = 0; i < text.length; i++) {
      const angle = currentAngle;
      const x = Math.cos(angle) * arcRadius;
      const y = Math.sin(angle) * arcRadius + arcRadius;
      const rotation = angle + Math.PI / 2;

      const shadowChar = new PIXI.Text(text[i], shadowStyle);
      shadowChar.resolution = 2;
      shadowChar.anchor.set(0.5);
      shadowChar.x = x;
      shadowChar.y = y + (primaryStyle.shadowOffsetY || 3);
      shadowChar.rotation = rotation;

      const topChar = new PIXI.Text(text[i], topStyle);
      topChar.resolution = 2;
      topChar.anchor.set(0.5);
      topChar.x = x;
      topChar.y = y;
      topChar.rotation = rotation;

      container.addChild(shadowChar);
      container.addChild(topChar);

      if (i < text.length - 1) {
        currentAngle += angularWidths[i] / 2 + angularWidths[i + 1] / 2;
      }
    }

    this.introSpine.addSlotObject("txt_slot_congrats", container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
  }

  initializePrimarySlotText(slotName, text, primaryStyle, slotText, fontSize) {
    const slot = this.introSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "800",
    };

    const shadowText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.shadow }),
    );
    shadowText.resolution = 2;
    shadowText.anchor.set(0.5);
    shadowText.y = primaryStyle.shadowOffsetY || 3;

    const topText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.top }),
    );
    topText.resolution = 2;
    topText.anchor.set(0.5);

    container.addChild(shadowText);
    container.addChild(topText);
    this.introSpine.addSlotObject(slotName, container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
  }

  updateButtonText() {
    if (!this.buttonText) return;

    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    this.buttonText.text = buttonText;
    if (this.buttonShadowText) {
      this.buttonShadowText.text = buttonText;
    }
  }

  showButton() {
    if (this.buttonContainer) {
      // Cancel any running button animation to prevent race conditions
      if (this.buttonAnim) {
        this.buttonAnim.pause();
        this.buttonAnim = null;
      }
      this.buttonAnim = anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
        complete: () => {
          this.buttonAnim = null;
        },
      });
    }

    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  hideButton() {
    if (this.buttonContainer) {
      // Cancel any running button animation (e.g. fade-in still in progress)
      if (this.buttonAnim) {
        this.buttonAnim.pause();
        this.buttonAnim = null;
      }
      this.buttonAnim = anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 200,
        easing: "easeOutQuad",
        complete: () => {
          this.buttonAnim = null;
        },
      });
    }
  }

  /**
   * Start the Royal Collect intro animation sequence.
   * Mirrors FreeSpinIntroDialogController.startSequence() exactly:
   * reset skeleton → set animation while hidden → show on next RAF → wait for click.
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.introSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;

      // Reset user interaction guards
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts from previous interrupted sequences
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Fade in background overlay
      this.overlayRef.current?.fadeInBackground();

      // Play intro sound
      soundManager.play(SOUND_NAME.free_spin_intro);

      // Ensure final positioning before showing
      if (this.introSpine && this.symbolHeight && this.symbolHeight > 0 && this.dimensions) {
        this.applyScaleAndCenter();
      }

      // Reset skeleton to clean slate — prevents flash of old/warmup pose
      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      // SKIP PROTECTION: Block skipping for the first second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Set animation WHILE STILL HIDDEN — lets GPU process new pose
      this.introSpine.state.setAnimation(0, "free_spin_intro_in", false);

      // Show on NEXT FRAME — old warmup pose is gone, new animation starts fresh
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.introSpine) {
          this.introSpine.alpha = 1;
        }
      });

      // Get intro_in animation duration
      const inDuration = this.getSpineAnimationDuration("free_spin_intro_in");

      // After intro_in completes, start looping intro_idle
      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.introSpine.state.setAnimation(0, "free_spin_intro_idle", true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  getSpineAnimationDuration(animationName) {
    if (
      !this.introSpine ||
      !this.introSpine.skeleton ||
      !this.introSpine.skeleton.data ||
      !this.introSpine.skeleton.data.animations
    ) {
      return 1000;
    }

    try {
      const animation = this.introSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000;
    } catch (error) {
      return 1000;
    }
  }

  handleUserClick() {
    if (!this.isPlaying || !this.introSpine?.state) {
      return false;
    }

    if (this.debounceTimeout) {
      return false;
    }

    if (!this.canSkipAnimation) {
      return false;
    }

    if (!this.isWaitingForClick) {
      return false;
    }

    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    this.playClosingAnimation();

    return true;
  }

  /**
   * Play closing animation — triggers scene transition on completion
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.introSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop intro sound
    soundManager.stop(SOUND_NAME.free_spin_intro);

    this.introSpine.state.clearListeners();

    this.introSpine.state.setAnimation(0, "free_spin_intro_out", false);
    this.introSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "free_spin_intro_out" && this.isPlaying) {
          // Start Royal Collect scene transition
          // NOTE: royalCollectTriggered is reset in BackgroundAnimation's
          // transition completion handler (mirrors Free Spin pattern)
          if (this.dispatch) {
            this.dispatch(setRoyalCollectTransitionActive(true));
          }
          this.finishAnimation();
        }
      },
    });
  }

  finishAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.introSpine.alpha = 0.001;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Force-cancel any running button animation and hide immediately
    if (this.buttonAnim) {
      this.buttonAnim.pause();
      this.buttonAnim = null;
    }
    if (this.buttonContainer) {
      this.buttonContainer.alpha = 0;
    }
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }

    // Clear spine state so nothing lingers
    if (this.introSpine?.state) {
      this.introSpine.state.clearListeners();
      this.introSpine.state.clearTracks();
    }

    // Fade out overlay and close dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(setShowRoyalCollectIntro(false));
      }
    });
  }

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    soundManager.stop(SOUND_NAME.free_spin_intro);

    if (this.introSpine) {
      if (this.introSpine.state) {
        this.introSpine.state.clearListeners();
        this.introSpine.state.clearTracks();
      }
      if (this.introSpine.parent) {
        this.introSpine.parent.removeChild(this.introSpine);
      }
      this.introSpine.destroy();
      this.introSpine = null;
    }

    if (this.buttonContainer) {
      if (this.buttonContainer.parent) {
        this.buttonContainer.parent.removeChild(this.buttonContainer);
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    if (this.textContainers) {
      this.textContainers.forEach((tc) => {
        if (tc.parent) tc.parent.removeChild(tc);
        tc.destroy({ children: true });
      });
      this.textContainers = null;
    }

    this.initialized = false;
    this.isPlaying = false;
  }
}

export default RoyalCollectIntroDialogController;
